import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate } from 'react-redux-i18n';

import { StoreState } from '../../../reducers';
import Accordion from '../../Accordion';
import { Wrapper, Row, Cell, Label, Icon } from './styles';
import SalesforcePopup from '../../../containers/ProfilePage/CRMSelector/SalesforcePopup';
import { activateSalesforce } from '../../../containers/ProfilePage/actions';
import { Api } from '../../../typings/api';
import Utils from '../../../utils';
import UserAgentUtils from '../../../utils/userAgent';
import NativeApi from '../../../utils/nativeApi';

interface CRMSelectorProps {
	crmProviders: Api.CRMProviderData[];
	isUserEditor: boolean;
	onChange: (value: Api.CRMProviders) => void;
	dispatch: Dispatch<StoreState>;
}

const getOptions = (crmProviders: Api.CRMProviderData[]) => ([
	{
		value: Api.CRMProviders.NONE,
		label: <Translate value="messages.none" />,
	},
	...crmProviders.map(({ provider }) => ({
		value: provider,
		label: provider.toLowerCase(),
	})),
]);

const openIntegrationUrl = (crmProvider: Api.CRMProviders, crmProviders: Api.CRMProviderData[]): void => {
	const DELAY_TO_OPEN_URL = 500;
	const enabledCrmProvider = crmProviders.find(({ provider }) => provider === crmProvider);

	if (enabledCrmProvider) {
		const { integrationUrl } = enabledCrmProvider;
		if (UserAgentUtils.isMobileApp) {
			setTimeout(() => NativeApi.openFrame(integrationUrl), DELAY_TO_OPEN_URL);
		} else {
			setTimeout(() => window.open(integrationUrl), DELAY_TO_OPEN_URL);
		}
	}
};

const CRMSelector = ({ crmProviders, isUserEditor, onChange, dispatch }: CRMSelectorProps) => {
	const enabledCrmProvider = crmProviders.find(({ enabled }) => enabled);
	const initialValue = enabledCrmProvider
		? enabledCrmProvider.provider
		: Api.CRMProviders.NONE;
	const [currentValue, setCurrentValue] = React.useState(initialValue);
	const [isSalesforcePopupDisplayed, setIsSalesforcePopupDisplayed] = React.useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value as Api.CRMProviders;
		setCurrentValue(value);
		onChange(value);

		const shouldOpenSalesforcePopup = value === Api.CRMProviders.SALESFORCE && !isUserEditor;

		if (shouldOpenSalesforcePopup) {
			setIsSalesforcePopupDisplayed(true);
		} else {
			openIntegrationUrl(value, crmProviders);
		}
	};

	const handleSalesforcePopupClose = () => {
		setIsSalesforcePopupDisplayed(false);
	};

	const handleSalesforcePopupConfirm = (clientId: string, clientSecret: string) => {
		dispatch(activateSalesforce(clientId, clientSecret, () => {
			setIsSalesforcePopupDisplayed(false);
			openIntegrationUrl(Api.CRMProviders.SALESFORCE, crmProviders);
		}));
	};

	React.useEffect(() => {
		if (currentValue !== initialValue) {
			setCurrentValue(initialValue);
		}
	}, [crmProviders]);

	return (
		<Accordion
			title="messages.crm"
			isOpened={true}
		>
			<Wrapper>
				{getOptions(crmProviders).map(({ value, label }) => {
					const isChecked = currentValue === value;
					return (
						<Row key={value}>
							<Cell isFirst={true}>
								<Label>
									{label}
								</Label>
							</Cell>
							<Cell>
								<Icon isChecked={isChecked}>
									<label>
										<input
											type="radio"
											name="CRM"
											value={value}
											checked={isChecked}
											onChange={handleChange}
										/>
										{isChecked && Utils.getSvgIcon('iconset_check-3-bold')}
									</label>
								</Icon>
							</Cell>
						</Row>
					);
				})}
			</Wrapper>
			{!isUserEditor &&
				<SalesforcePopup
					isDisplayed={isSalesforcePopupDisplayed}
					onConfirm={handleSalesforcePopupConfirm}
					onCancel={handleSalesforcePopupClose}
				/>
			}
		</Accordion>
	);
};

export default CRMSelector;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ProfilePage/CRMSelector/index.tsx